import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  ArrowLeft,
  FileText,
  Car,
  Calendar,
  DollarSign,
  CheckCircle,
  Clock,
  MessageSquare,
  Tag,
  BellOff,
  Bell,
  X,
  User
} from 'lucide-react';

// Input masks
const maskCPF = (value) => {
  return value
    .replace(/\D/g, '')
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const maskCNPJ = (value) => {
  return value
    .replace(/\D/g, '')
    .slice(0, 14)
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

const maskPhone = (value) => {
  return value
    .replace(/\D/g, '')
    .slice(0, 11)
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{1})(\d{4})(\d{4})$/, '$1 $2-$3');
};

const maskCEP = (value) => {
  return value
    .replace(/\D/g, '')
    .slice(0, 8)
    .replace(/(\d{5})(\d)/, '$1-$2');
};

// Client List View
const ClientListView = ({ onSelectClient }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [sortOrder, setSortOrder] = useState('asc');
  // Bulk selection (para aplicar tags em massa)
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkTag, setBulkTag] = useState('');  // tag única
  const [bulkApplying, setBulkApplying] = useState(false);
  const [allTagsList, setAllTagsList] = useState([]);
  
  useEffect(() => {
    if (bulkMode) {
      axios.get(`${API}/client-tags`, { withCredentials: true })
        .then((r) => setAllTagsList(r.data || []))
        .catch(() => {});
    }
  }, [bulkMode, API]);

  const toggleBulkSelect = (id) => setBulkSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const aplicarBulkTags = async () => {
    if (bulkSelected.size === 0) { toast.error('Selecione clientes primeiro'); return; }
    if (bulkTag === undefined || bulkTag === null) { toast.error('Escolha a tag (ou "Sem tag" para limpar)'); return; }
    setBulkApplying(true);
    try {
      const res = await axios.post(`${API}/client-tags/bulk-apply`,
        { client_ids: Array.from(bulkSelected), tag: bulkTag || null },
        { withCredentials: true }
      );
      const acao = bulkTag ? `Tag "${bulkTag}" aplicada a` : 'Tag removida de';
      toast.success(`${acao} ${res.data.modificados} cliente(s)`);
      setBulkMode(false); setBulkSelected(new Set()); setBulkTag('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao aplicar tag');
    } finally { setBulkApplying(false); }
  };
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formMode, setFormMode] = useState('create');
  
  // Form state
  const [formData, setFormData] = useState({
    tipo_pessoa: 'fisica', // fisica ou juridica
    nome: '',
    cpf_cnpj: '',
    telefone: '',
    telefones_adicionais: [{ nome: '', telefone: '' }],
    email: '',
    endereco: {
      cep: '',
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      ponto_referencia: ''
    }
  });
  const [loadingCep, setLoadingCep] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadClients();
  }, [search, statusFilter, sortOrder]);

  const loadClients = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'todos') params.append('status', statusFilter);
      params.append('sort_by', 'nome');
      params.append('sort_order', sortOrder);
      
      const response = await axios.get(`${API}/clients?${params}`, { withCredentials: true });
      setClients(response.data);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleCepSearch = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;
    
    setLoadingCep(true);
    try {
      const response = await axios.get(`${API}/cep/${cleanCep}`, { withCredentials: true });
      setFormData(prev => ({
        ...prev,
        endereco: {
          ...prev.endereco,
          cep: response.data.cep,
          logradouro: response.data.logradouro,
          bairro: response.data.bairro,
          cidade: response.data.cidade,
          estado: response.data.estado
        }
      }));
      toast.success('Endereço encontrado!');
    } catch (error) {
      toast.error('CEP não encontrado');
    } finally {
      setLoadingCep(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    const data = {
      ...formData,
      telefones_adicionais: formData.telefones_adicionais.filter(t => t.telefone.trim() !== '')
    };
    
    try {
      if (formMode === 'create') {
        await axios.post(`${API}/clients`, data, { withCredentials: true });
        toast.success('Cliente cadastrado com sucesso!');
      } else {
        await axios.put(`${API}/clients/${selectedClient.client_id}`, data, { withCredentials: true });
        toast.success('Cliente atualizado com sucesso!');
      }
      setShowModal(false);
      resetForm();
      loadClients();
    } catch (error) {
      console.error('Error saving client:', error.response?.data);
      
      // Handle validation errors from Pydantic
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array of validation errors
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(err => {
            const field = err.loc ? err.loc.join('.') : 'campo';
            return `${field}: ${err.msg}`;
          }).join(', ');
          toast.error(`Erro de validação: ${errorMessages}`);
        } else if (typeof detail === 'string') {
          // If detail is a string message
          toast.error(detail);
        } else {
          // If detail is an object
          toast.error('Erro ao salvar cliente. Verifique os campos.');
        }
      } else {
        toast.error('Erro ao salvar cliente');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/clients/${selectedClient.client_id}`, { withCredentials: true });
      toast.success('Cliente excluído com sucesso!');
      setShowDeleteDialog(false);
      loadClients();
    } catch (error) {
      toast.error('Erro ao excluir cliente');
    }
  };

  const resetForm = () => {
    setFormData({
      tipo_pessoa: 'fisica',
      nome: '',
      cpf_cnpj: '',
      telefone: '',
      telefones_adicionais: [{ nome: '', telefone: '' }],
      email: '',
      endereco: {
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        ponto_referencia: ''
      }
    });
  };

  const openCreateModal = () => {
    setFormMode('create');
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (client, e) => {
    e?.stopPropagation();
    setFormMode('edit');
    setSelectedClient(client);
    // Detectar tipo de pessoa pelo tamanho do CPF/CNPJ
    const cpfCnpj = client.cpf_cnpj?.replace(/\D/g, '') || '';
    const tipoPessoa = cpfCnpj.length > 11 ? 'juridica' : 'fisica';
    setFormData({
      tipo_pessoa: tipoPessoa,
      nome: client.nome,
      cpf_cnpj: client.cpf_cnpj,
      telefone: client.telefone,
      telefones_adicionais: client.telefones_adicionais?.length > 0 
        ? client.telefones_adicionais 
        : [{ nome: '', telefone: '' }],
      email: client.email,
      endereco: client.endereco
    });
    setShowModal(true);
  };

  const openDeleteDialog = (client, e) => {
    e?.stopPropagation();
    setSelectedClient(client);
    setShowDeleteDialog(true);
  };

  const handleCpfCnpjChange = (value) => {
    if (formData.tipo_pessoa === 'juridica') {
      setFormData({ ...formData, cpf_cnpj: maskCNPJ(value) });
    } else {
      setFormData({ ...formData, cpf_cnpj: maskCPF(value) });
    }
  };

  const addContactField = () => {
    setFormData({
      ...formData,
      telefones_adicionais: [...formData.telefones_adicionais, { nome: '', telefone: '' }]
    });
  };

  const updateAdditionalContact = (index, field, value) => {
    const contacts = [...formData.telefones_adicionais];
    if (field === 'telefone') {
      contacts[index][field] = maskPhone(value);
    } else {
      contacts[index][field] = value;
    }
    setFormData({ ...formData, telefones_adicionais: contacts });
  };

  const removeContactField = (index) => {
    const contacts = formData.telefones_adicionais.filter((_, i) => i !== index);
    setFormData({ 
      ...formData, 
      telefones_adicionais: contacts.length > 0 ? contacts : [{ nome: '', telefone: '' }] 
    });
  };

  const getInitials = (name) => {
    if (!name) return 'C';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 
      'bg-pink-500', 'bg-teal-500', 'bg-indigo-500', 'bg-red-500'
    ];
    const index = name ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope]">Gestão de Clientes</h1>
          <p className="text-gray-500 text-sm">Cadastre e gerencie seus clientes</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={bulkMode ? 'default' : 'outline'}
            onClick={() => { setBulkMode(!bulkMode); setBulkSelected(new Set()); }}
            className={bulkMode ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}
            data-testid="btn-bulk-mode"
          >
            <Tag className="w-4 h-4 mr-2" />
            {bulkMode ? `Selecionando (${bulkSelected.size})` : 'Aplicar Tags em Massa'}
          </Button>
          <Button onClick={openCreateModal} data-testid="add-client-button">
            <Plus className="w-4 h-4 mr-2" />
            Novo Cliente
          </Button>
        </div>
      </div>

      {/* Barra de Bulk Tag (única) */}
      {bulkMode && (
        <Card className="border-2 border-indigo-300 bg-indigo-50/40">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-sm font-semibold text-indigo-900">
                  {bulkSelected.size} cliente(s) selecionado(s)
                </p>
                <span className="text-xs text-slate-500">·</span>
                <p className="text-xs text-slate-600">Aplicar tag:</p>
                <div className="flex gap-1.5 flex-wrap">
                  <button
                    onClick={() => setBulkTag('')}
                    className={`text-xs px-2 py-1 rounded-full border transition ${bulkTag === '' ? 'bg-slate-700 text-white border-slate-800' : 'bg-white border-slate-300 hover:border-slate-400 text-slate-600'}`}
                    data-testid="bulk-tag-none"
                  >
                    × Sem tag (PADRÃO)
                  </button>
                  {allTagsList.length === 0 ? (
                    <span className="text-xs text-slate-400 italic">Nenhuma tag — vá em Notificações → Mensagens</span>
                  ) : allTagsList.filter((t) => !t.is_default).map((t) => {
                    const sel = bulkTag === t.nome;
                    return (
                      <button key={t.nome}
                        onClick={() => setBulkTag(t.nome)}
                        className={`text-xs px-2 py-1 rounded-full border transition ${sel ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white border-slate-300 hover:border-indigo-400 text-slate-700'}`}
                        data-testid={`bulk-tag-${t.nome}`}>
                        #{t.nome}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setBulkMode(false); setBulkSelected(new Set()); setBulkTag(''); }}>Cancelar</Button>
                <Button size="sm" onClick={aplicarBulkTags} disabled={bulkApplying || bulkSelected.size === 0}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="btn-aplicar-bulk">
                  {bulkApplying ? <span className="animate-spin h-3 w-3 border-2 border-white border-b-transparent rounded-full" /> : null}
                  Aplicar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por nome, CPF ou email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-clients"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="ativo">Ativo</SelectItem>
                <SelectItem value="inativo">Inativo</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-[100px]" data-testid="sort-order">
                <SelectValue placeholder="Ordem" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asc">A-Z</SelectItem>
                <SelectItem value="desc">Z-A</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Client Count */}
      <div className="flex items-center gap-2 text-gray-600">
        <Users className="w-5 h-5" />
        <span className="font-medium">Lista de Clientes ({clients.length})</span>
      </div>

      {/* Client Cards Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : clients.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Users className="w-12 h-12 mb-2 opacity-50" />
            <p>Nenhum cliente encontrado</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((client) => (
            <Card 
              key={client.client_id} 
              className={`hover:shadow-lg transition-shadow cursor-pointer ${bulkMode && bulkSelected.has(client.client_id) ? 'ring-2 ring-indigo-500' : ''}`}
              onClick={() => bulkMode ? toggleBulkSelect(client.client_id) : onSelectClient(client.client_id)}
              data-testid={`client-card-${client.client_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {bulkMode && (
                    <input
                      type="checkbox"
                      checked={bulkSelected.has(client.client_id)}
                      onChange={() => toggleBulkSelect(client.client_id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      data-testid={`bulk-check-${client.client_id}`}
                    />
                  )}
                  {/* Avatar */}
                  <div className={`w-12 h-12 rounded-full ${getAvatarColor(client.nome)} flex items-center justify-center text-white font-semibold`}>
                    {getInitials(client.nome)}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-gray-900 truncate">{client.nome}</h3>
                        <p className="text-sm text-gray-500">
                          {client.endereco?.cidade}/{client.endereco?.estado}
                        </p>
                      </div>
                      <Badge 
                        variant={client.status === 'ativo' ? 'default' : 'secondary'}
                        className={client.status === 'ativo' ? 'bg-green-500' : 'bg-gray-400'}
                      >
                        {client.status === 'ativo' ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>

                    {/* Tag única do cliente */}
                    {client.tag && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        <span className="text-[10px] bg-indigo-100 text-indigo-800 border border-indigo-200 px-1.5 py-0.5 rounded font-medium">
                          #{client.tag}
                        </span>
                      </div>
                    )}
                    
                    <div className="space-y-1 mt-3 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <FileText className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{client.cpf_cnpj}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <span>{client.telefone}</span>
                      </div>
                      {client.email && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{client.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Action Button */}
                <Button 
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={(e) => openEditModal(client, e)}
                  data-testid={`edit-client-${client.client_id}`}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar Cliente
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {formMode === 'create' ? 'Cadastrar Cliente' : 'Editar Cliente'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Tipo de Pessoa */}
            <div className="flex gap-4 p-3 bg-gray-50 rounded-lg">
              <Label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tipo_pessoa"
                  value="fisica"
                  checked={formData.tipo_pessoa === 'fisica'}
                  onChange={(e) => setFormData({ ...formData, tipo_pessoa: e.target.value, cpf_cnpj: '' })}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="font-medium">Pessoa Física</span>
              </Label>
              <Label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tipo_pessoa"
                  value="juridica"
                  checked={formData.tipo_pessoa === 'juridica'}
                  onChange={(e) => setFormData({ ...formData, tipo_pessoa: e.target.value, cpf_cnpj: '' })}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="font-medium">Pessoa Jurídica</span>
              </Label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="nome">{formData.tipo_pessoa === 'juridica' ? 'Razão Social *' : 'Nome Completo *'}</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                  data-testid="client-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="cpf_cnpj">{formData.tipo_pessoa === 'juridica' ? 'CNPJ *' : 'CPF *'}</Label>
                <Input
                  id="cpf_cnpj"
                  value={formData.cpf_cnpj}
                  onChange={(e) => handleCpfCnpjChange(e.target.value)}
                  placeholder={formData.tipo_pessoa === 'juridica' ? '00.000.000/0000-00' : '000.000.000-00'}
                  required
                  disabled={formMode === 'edit'}
                  data-testid="client-cpf-input"
                />
              </div>
              
              <div>
                <Label htmlFor="telefone">Telefone *</Label>
                <Input
                  id="telefone"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: maskPhone(e.target.value) })}
                  placeholder="(00) 0 0000-0000"
                  required
                  data-testid="client-phone-input"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="email">E-mail *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="client-email-input"
                />
              </div>

              {/* Additional Contacts */}
              <div className="md:col-span-2 space-y-2">
                <Label>Telefones Adicionais</Label>
                {formData.telefones_adicionais.map((contact, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={contact.nome}
                      onChange={(e) => updateAdditionalContact(index, 'nome', e.target.value)}
                      placeholder="Nome do contato"
                      className="flex-1"
                    />
                    <Input
                      value={contact.telefone}
                      onChange={(e) => updateAdditionalContact(index, 'telefone', e.target.value)}
                      placeholder="(00) 0 0000-0000"
                      className="flex-1"
                    />
                    {formData.telefones_adicionais.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeContactField(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addContactField}>
                  <Plus className="w-4 h-4 mr-1" /> Adicionar telefone
                </Button>
              </div>

              {/* Address */}
              <div className="md:col-span-2 border-t pt-4 mt-2">
                <h3 className="font-semibold mb-3">Endereço</h3>
              </div>
              
              <div>
                <Label htmlFor="cep">CEP *</Label>
                <div className="flex gap-2">
                  <Input
                    id="cep"
                    value={formData.endereco.cep}
                    onChange={(e) => {
                      const masked = maskCEP(e.target.value);
                      setFormData({ 
                        ...formData, 
                        endereco: { ...formData.endereco, cep: masked }
                      });
                      if (masked.replace(/\D/g, '').length === 8) {
                        handleCepSearch(masked);
                      }
                    }}
                    placeholder="00000-000"
                    required
                    data-testid="client-cep-input"
                  />
                  {loadingCep && (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <Label htmlFor="logradouro">Logradouro *</Label>
                <Input
                  id="logradouro"
                  value={formData.endereco.logradouro}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    endereco: { ...formData.endereco, logradouro: e.target.value }
                  })}
                  required
                  data-testid="client-street-input"
                />
              </div>
              
              <div>
                <Label htmlFor="numero">Número</Label>
                <Input
                  id="numero"
                  value={formData.endereco.numero}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    endereco: { ...formData.endereco, numero: e.target.value }
                  })}
                  data-testid="client-number-input"
                />
              </div>
              
              <div>
                <Label htmlFor="complemento">Complemento</Label>
                <Input
                  id="complemento"
                  value={formData.endereco.complemento}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    endereco: { ...formData.endereco, complemento: e.target.value }
                  })}
                  data-testid="client-complement-input"
                />
              </div>
              
              <div>
                <Label htmlFor="bairro">Bairro *</Label>
                <Input
                  id="bairro"
                  value={formData.endereco.bairro}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    endereco: { ...formData.endereco, bairro: e.target.value }
                  })}
                  required
                  data-testid="client-neighborhood-input"
                />
              </div>
              
              <div>
                <Label htmlFor="cidade">Cidade *</Label>
                <Input
                  id="cidade"
                  value={formData.endereco.cidade}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    endereco: { ...formData.endereco, cidade: e.target.value }
                  })}
                  required
                  data-testid="client-city-input"
                />
              </div>
              
              <div>
                <Label htmlFor="estado">Estado *</Label>
                <Input
                  id="estado"
                  value={formData.endereco.estado}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    endereco: { ...formData.endereco, estado: e.target.value.toUpperCase().slice(0, 2) }
                  })}
                  maxLength={2}
                  required
                  data-testid="client-state-input"
                />
              </div>
              
              <div>
                <Label htmlFor="ponto_referencia">Ponto de Referência</Label>
                <Input
                  id="ponto_referencia"
                  value={formData.endereco.ponto_referencia}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    endereco: { ...formData.endereco, ponto_referencia: e.target.value }
                  })}
                  data-testid="client-reference-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-client-button">
                {submitting ? 'Salvando...' : formMode === 'create' ? 'Cadastrar' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o cliente "{selectedClient?.nome}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-client"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Client Detail View
const ClientDetailView = ({ clientId, onBack }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoiceFilter, setInvoiceFilter] = useState('todas');
  const [newNote, setNewNote] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (clientId) {
      loadClientData();
    }
  }, [clientId, invoiceFilter]);

  const loadClientData = async () => {
    setLoading(true);
    try {
      const [clientRes, contractsRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`, { withCredentials: true }),
        axios.get(`${API}/clients/${clientId}/contracts`, { withCredentials: true }),
        axios.get(`${API}/clients/${clientId}/invoices?status=${invoiceFilter}`, { withCredentials: true })
      ]);
      
      setClient(clientRes.data);
      setContracts(contractsRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados do cliente');
      onBack();
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      await axios.post(`${API}/clients/${clientId}/notes?content=${encodeURIComponent(newNote)}`, {}, { withCredentials: true });
      toast.success('Nota adicionada!');
      setNewNote('');
      loadClientData();
    } catch (error) {
      toast.error('Erro ao adicionar nota');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/clients/${clientId}/notes/${noteId}`, { withCredentials: true });
      toast.success('Nota excluída!');
      loadClientData();
    } catch (error) {
      toast.error('Erro ao excluir nota');
    }
  };

  const handleDeleteClient = async () => {
    try {
      await axios.delete(`${API}/clients/${clientId}`, { withCredentials: true });
      toast.success('Cliente excluído!');
      onBack();
    } catch (error) {
      toast.error('Erro ao excluir cliente');
    }
  };

  const handleToggleBlacklist = async (nao_notificar, ate = null, motivo = null) => {
    try {
      await axios.patch(
        `${API}/clients/${clientId}/notificacao`,
        { nao_notificar, nao_notificar_ate: ate, motivo },
        { withCredentials: true }
      );
      toast.success(nao_notificar ? 'Notificações pausadas para este cliente' : 'Notificações reactivadas');
      loadClientData();
    } catch {
      toast.error('Erro ao actualizar preferência de notificação');
    }
  };

  const [allTags, setAllTags] = useState([]);
  const loadTags = async () => {
    try {
      const res = await axios.get(`${API}/client-tags`, { withCredentials: true });
      setAllTags(res.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadTags(); }, []); // eslint-disable-line

  const handleSetTag = async (tagNome) => {
    try {
      await axios.patch(`${API}/clients/${clientId}/tag`, { tag: tagNome || null }, { withCredentials: true });
      toast.success(tagNome ? `Tag #${tagNome} aplicada` : 'Tag removida (volta a usar régua PADRÃO)');
      loadClientData();
    } catch {
      toast.error('Erro ao atualizar tag');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const getInitials = (name) => {
    if (!name) return 'C';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const totalContractValue = contracts.reduce((acc, c) => acc + (c.valor_total || 0), 0);
  const totalInvoiceValue = invoices.reduce((acc, i) => acc + (i.valor_atualizado || i.valor_original || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-[Manrope]">{client.nome}</h1>
            <p className="text-sm text-gray-500">{client.cpf_cnpj}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="text-red-600"
            onClick={() => setShowDeleteDialog(true)}
            data-testid="delete-client-button"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Excluir
          </Button>
          <Button onClick={() => navigate(`/clientes?edit=${clientId}`)} data-testid="edit-client-button">
            <Edit className="w-4 h-4 mr-2" />
            Editar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Client Info */}
        <div className="space-y-6">
          {/* Client Information Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Informações do Cliente</CardTitle>
              <Badge 
                variant={client.status === 'ativo' ? 'default' : 'secondary'}
                className={client.status === 'ativo' ? 'bg-green-500' : 'bg-gray-400'}
              >
                {client.status === 'ativo' ? 'Ativo' : 'Inativo'}
              </Badge>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center text-white text-xl font-bold">
                  {getInitials(client.nome)}
                </div>
                <div>
                  <h3 className="font-semibold">{client.nome}</h3>
                  <p className="text-sm text-gray-500">
                    {client.cpf_cnpj?.length > 14 ? 'Pessoa Jurídica' : 'Pessoa Física'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tag única / Régua de Cobrança */}
          <Card data-testid="card-tags-cliente">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-600" />
                Régua de Cobrança (Tag)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {client?.tag ? (
                <div>
                  <p className="text-xs text-slate-500 mb-1.5">Tag atual — define a régua de mensagens</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(() => {
                      const tag = allTags.find((x) => x.nome === client.tag);
                      const corMap = {
                        blue:'bg-blue-100 text-blue-800 border-blue-300',
                        green:'bg-green-100 text-green-800 border-green-300',
                        red:'bg-red-100 text-red-800 border-red-300',
                        yellow:'bg-yellow-100 text-yellow-800 border-yellow-300',
                        purple:'bg-purple-100 text-purple-800 border-purple-300',
                        pink:'bg-pink-100 text-pink-800 border-pink-300',
                        gray:'bg-gray-100 text-gray-800 border-gray-300',
                        orange:'bg-orange-100 text-orange-800 border-orange-300',
                        indigo:'bg-indigo-100 text-indigo-800 border-indigo-300',
                        teal:'bg-teal-100 text-teal-800 border-teal-300',
                      };
                      const cor = corMap[tag?.cor || 'indigo'];
                      return (
                        <button onClick={() => handleSetTag(null)}
                          className={`text-sm font-semibold px-3 py-1.5 rounded-full border ${cor} hover:opacity-80 transition flex items-center gap-1.5`}
                          data-testid={`tag-aplicada-${client.tag}`}
                          title="Clique para remover tag (volta para PADRÃO)">
                          #{client.tag} <X className="w-3.5 h-3.5 opacity-60" />
                        </button>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-500 bg-slate-50 rounded p-2">
                  Sem tag — este cliente recebe a régua <strong>PADRÃO</strong>.
                </div>
              )}

              {allTags.filter((t) => !t.is_default).length > 0 ? (
                <div>
                  <p className="text-xs text-slate-500 mb-1.5">{client?.tag ? 'Trocar para' : 'Aplicar tag'}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {allTags
                      .filter((t) => !t.is_default && t.nome !== client?.tag)
                      .map((t) => (
                        <button key={t._id} onClick={() => handleSetTag(t.nome)}
                          className="text-xs px-2 py-1 rounded-full border border-dashed border-slate-300 text-slate-600 hover:border-indigo-400 hover:bg-indigo-50 transition"
                          data-testid={`btn-add-tag-${t.nome}`}>
                          → #{t.nome}
                        </button>
                      ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-3 text-xs text-slate-400">
                  <p>Nenhuma tag adicional cadastrada.</p>
                  <p className="mt-1">Vá em <strong>Notificações → Mensagens</strong> para criar.</p>
                </div>
              )}

              <div className="text-[11px] text-slate-500 leading-relaxed bg-slate-50 rounded-lg p-2.5">
                <strong>💡 Como funciona:</strong> cada tag tem uma régua própria de mensagens por faixa de atraso.
                Aplique uma tag (ex: #VIP) → o cliente recebe automaticamente as mensagens dessa régua a cada dia de atraso.
              </div>
            </CardContent>
          </Card>

          {/* Automação de Notificações (Blacklist individual) */}
          <Card data-testid="card-notificacao-cliente">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                {client.nao_notificar ? <BellOff className="w-4 h-4 text-amber-600" /> : <Bell className="w-4 h-4 text-green-600" />}
                Automação de Notificações
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {client.nao_notificar ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <BellOff className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                    <div className="flex-1 text-sm">
                      <p className="font-semibold text-amber-900">Notificações PAUSADAS</p>
                      {client.nao_notificar_ate
                        ? <p className="text-xs text-amber-700 mt-0.5">Até <strong>{formatDate(client.nao_notificar_ate)}</strong></p>
                        : <p className="text-xs text-amber-700 mt-0.5">Indefinidamente</p>}
                      {client.nao_notificar_motivo && (
                        <p className="text-xs text-amber-800 mt-1 italic">"{client.nao_notificar_motivo}"</p>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleToggleBlacklist(false)}
                    className="w-full gap-2 border-green-300 text-green-700 hover:bg-green-50"
                    data-testid="btn-reactivate-notifications"
                  >
                    <Bell className="w-4 h-4" />
                    Reativar notificações
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Este cliente <strong>recebe</strong> automaticamente as cobranças de WhatsApp conforme as regras de automação.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const motivo = window.prompt('Motivo da pausa (opcional):', 'Acordo especial');
                        if (motivo !== null) handleToggleBlacklist(true, null, motivo || null);
                      }}
                      className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="btn-pause-notifications-indef"
                    >
                      <BellOff className="w-3.5 h-3.5" />
                      Pausar indef.
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const d30 = new Date(); d30.setDate(d30.getDate() + 30);
                        const ate = d30.toISOString().slice(0, 10);
                        const motivo = window.prompt('Motivo da pausa (opcional):', 'Cliente pediu 30 dias de tolerância');
                        if (motivo !== null) handleToggleBlacklist(true, ate, motivo || null);
                      }}
                      className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="btn-pause-notifications-30d"
                    >
                      <BellOff className="w-3.5 h-3.5" />
                      Pausar 30 dias
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Documents */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Documentos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">CPF/CNPJ</p>
                  <p className="font-medium">{client.cpf_cnpj}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contacts */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Contatos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Telefone Principal</p>
                  <p className="font-medium">{client.telefone}</p>
                </div>
              </div>
              {client.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">E-mail</p>
                    <p className="font-medium">{client.email}</p>
                  </div>
                </div>
              )}
              {client.telefones_adicionais?.map((contact, i) => (
                contact.telefone && (
                  <div key={i} className="flex items-center gap-3">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">{contact.nome || 'Contato Adicional'}</p>
                      <p className="font-medium">{contact.telefone}</p>
                    </div>
                  </div>
                )
              ))}
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Endereço</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium">
                    {client.endereco?.logradouro}
                    {client.endereco?.numero && `, ${client.endereco.numero}`}
                    {client.endereco?.complemento && ` (${client.endereco.complemento})`}
                  </p>
                  <p className="text-gray-600">
                    {client.endereco?.bairro} - {client.endereco?.cidade}/{client.endereco?.estado}
                  </p>
                  <p className="text-sm text-gray-500">CEP: {client.endereco?.cep}</p>
                  {client.endereco?.ponto_referencia && (
                    <p className="text-sm text-gray-500 mt-1">
                      Ref: {client.endereco.ponto_referencia}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Contracts & Invoices */}
        <div className="space-y-6">
          {/* Active Contracts */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Car className="w-5 h-5" />
                Contratos Ativos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {contracts.filter(c => c.status === 'ativo').length === 0 ? (
                <p className="text-gray-500 text-center py-4">Nenhum contrato ativo</p>
              ) : (
                <div className="space-y-3">
                  {contracts.filter(c => c.status === 'ativo').map((contract) => (
                    <div key={contract.contract_id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Car className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">{contract.vehicle_info}</span>
                      </div>
                      <p className="text-sm text-gray-500">ID: {contract.contract_id}</p>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="text-sm text-gray-500">Valor Total</span>
                    <span className="font-semibold text-green-600">{formatCurrency(totalContractValue)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invoices */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Faturas</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Tabs value={invoiceFilter} onValueChange={setInvoiceFilter} className="w-full">
                <div className="px-4 pt-2">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="todas">Todas</TabsTrigger>
                    <TabsTrigger value="pendente">Pendentes</TabsTrigger>
                    <TabsTrigger value="paga">Pagas</TabsTrigger>
                  </TabsList>
                </div>
                
                <div className="mt-4">
                  {invoices.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Nenhuma fatura encontrada</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Veículo</TableHead>
                            <TableHead>Vencimento</TableHead>
                            <TableHead>Valor</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Pagamento</TableHead>
                            <TableHead>Multa</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invoices.map((invoice) => (
                            <TableRow key={invoice.invoice_id}>
                              <TableCell className="font-medium text-sm">
                                {invoice.vehicle_info}
                              </TableCell>
                              <TableCell>{formatDate(invoice.vencimento)}</TableCell>
                              <TableCell>{formatCurrency(invoice.valor_original)}</TableCell>
                              <TableCell>
                                <Badge 
                                  className={
                                    invoice.status === 'paga' ? 'bg-green-500' : 
                                    invoice.status === 'vencida' ? 'bg-red-500' : 
                                    'bg-orange-500'
                                  }
                                >
                                  {invoice.status === 'paga' && <CheckCircle className="w-3 h-3 mr-1" />}
                                  {invoice.status !== 'paga' && <Clock className="w-3 h-3 mr-1" />}
                                  {invoice.status === 'paga' ? 'Pago' : invoice.status === 'vencida' ? 'Vencida' : 'Pendente'}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {invoice.data_pagamento ? formatDate(invoice.data_pagamento) : '-'}
                              </TableCell>
                              <TableCell>
                                {invoice.multa > 0 ? formatCurrency(invoice.multa) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      
                      {/* Total Row */}
                      <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
                        <span className="font-medium">Total (todas as faturas)</span>
                        <span className="font-bold text-lg">{formatCurrency(totalInvoiceValue)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </Tabs>
            </CardContent>
          </Card>

          {/* Internal Notes */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Notas Internas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add Note Form */}
              <div className="flex gap-2">
                <Input
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Adicionar uma nota..."
                  className="flex-1"
                  data-testid="note-input"
                />
                <Button onClick={handleAddNote} size="icon" data-testid="add-note-button">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Notes List */}
              {client.notas?.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma nota registrada</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {client.notas?.map((note) => (
                    <div key={note.note_id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm">{note.content}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {note.created_by} - {new Date(note.created_at).toLocaleString('pt-BR')}
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={() => handleDeleteNote(note.note_id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o cliente "{client?.nome}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteClient}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Main Component with Routing
export default function ClientsPage() {
  const [selectedClientId, setSelectedClientId] = useState(null);

  if (selectedClientId) {
    return (
      <ClientDetailView 
        clientId={selectedClientId} 
        onBack={() => setSelectedClientId(null)} 
      />
    );
  }

  return <ClientListView onSelectClient={setSelectedClientId} />;
}
